import { useEffect } from 'react';
import Header from '../../components/feature/Header';

const AboutPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const values = [
    {
      icon: 'ri-shield-check-line',
      title: 'Quality Excellence',
      description: 'We maintain the highest standards in every project, ensuring superior quality and durability in all our construction and trading operations.',
    },
    {
      icon: 'ri-team-line',
      title: 'Professional Team',
      description: 'Our experienced team of engineers, contractors, and specialists brings decades of combined expertise to deliver exceptional results.',
    },
    {
      icon: 'ri-time-line',
      title: 'Timely Delivery',
      description: 'We pride ourselves on completing projects on schedule, respecting deadlines while maintaining uncompromising quality standards.',
    },
    {
      icon: 'ri-customer-service-line',
      title: 'Client Satisfaction',
      description: 'Customer satisfaction is our priority. We work closely with clients to understand their needs and exceed expectations.',
    },
  ];

  const stats = [
    { number: '500+', label: 'Projects Completed' },
    { number: '25+', label: 'Years Experience' },
    { number: '200+', label: 'Expert Team' },
    { number: '98%', label: 'Client Satisfaction' },
  ];

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/722a3908018708d7e7a1ba7f9d820c4a.jpg"
            alt="About Raissy"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        <div className="relative z-10 text-center text-white px-6">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">About Raissy</h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto">
            Building Excellence Through Innovation and Integrity
          </p>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-8 lg:px-12 max-w-[1140px]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-[#1a1a1a] mb-6">
                Who We Are
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Raissy is a leading trading and contracting company with over 25 years of experience in delivering world-class construction projects and comprehensive trading solutions. We have established ourselves as a trusted partner for clients across various industries.
              </p>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Our commitment to excellence, innovation, and sustainability has enabled us to successfully complete over 500 projects, ranging from commercial buildings and residential complexes to large-scale infrastructure developments.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                With a team of highly skilled professionals and state-of-the-art equipment, we continue to set new standards in the construction and trading industry, delivering value and quality to our clients.
              </p>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=diverse%20professional%20construction%20team%20engineers%20architects%20contractors%20in%20modern%20office%20reviewing%20building%20plans%20blueprints%20collaboration%20teamwork%20bright%20clean%20minimalist%20background&width=800&height=600&seq=raissy-about-team-602&orientation=landscape"
                  alt="Our Team"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-[#0891b2]">
        <div className="container mx-auto px-8 lg:px-12 max-w-[1140px]">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-5xl md:text-6xl font-bold text-white mb-3">
                  {stat.number}
                </div>
                <div className="text-lg text-white/90">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-8 lg:px-12 max-w-[1140px]">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#1a1a1a] mb-6">
              Our Core Values
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              The principles that guide our work and define our commitment to excellence
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-lg shadow-sm hover:shadow-lg transition-shadow"
              >
                <div className="w-16 h-16 bg-[#0066B3]/10 rounded-lg flex items-center justify-center mb-6">
                  <i className={`${value.icon} text-3xl text-[#0066B3]`}></i>
                </div>
                <h3 className="text-xl font-bold text-[#1a1a1a] mb-4">
                  {value.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24 bg-[#f5f5f5]">
        <div className="container mx-auto px-8 lg:px-12 max-w-[1140px]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="bg-gradient-to-br from-[#0891b2] to-[#0e7490] p-12 rounded-lg text-white">
              <div className="w-16 h-16 bg-white/20 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-eye-line text-3xl text-white"></i>
              </div>
              <h3 className="text-3xl font-bold mb-6">Our Vision</h3>
              <p className="text-lg text-white/90 leading-relaxed">
                To be the most trusted and innovative trading and contracting company in the region, recognized for delivering exceptional quality, sustainable solutions, and creating lasting value for our clients and communities.
              </p>
            </div>

            <div className="bg-gradient-to-br from-[#1a1a1a] to-[#4a4a4a] p-12 rounded-lg text-white">
              <div className="w-16 h-16 bg-white/20 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-compass-line text-3xl text-white"></i>
              </div>
              <h3 className="text-3xl font-bold mb-6">Our Mission</h3>
              <p className="text-lg text-white/90 leading-relaxed">
                To deliver world-class construction and trading services through innovation, expertise, and unwavering commitment to quality. We strive to exceed client expectations while maintaining the highest standards of safety, sustainability, and professional excellence.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-8 lg:px-12 max-w-[1140px] text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-[#1a1a1a] mb-6">
            Ready to Start Your Project?
          </h2>
          <p className="text-lg text-[#4a4a4a] mb-8 max-w-2xl mx-auto">
            Let's discuss how we can help bring your vision to life with our expertise and dedication
          </p>
          <a
            href="/contact"
            className="inline-block bg-[#0891b2] text-white px-10 py-4 rounded-md text-base font-semibold hover:bg-[#0e7490] transition-colors whitespace-nowrap cursor-pointer"
          >
            Contact Us Today
          </a>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
